export type ExampleBranch = {
  label: string;
  question: string;
  answers: [string, string];
  results: [string, string];
  next: string;
};

export type SolutionExample = {
  title: string;
  brief: string;
  question: string;
  branches: [ExampleBranch, ExampleBranch, ExampleBranch];
  fields: string[];
  logic: string;
  destination: string;
  measure: string;
  boundary: string;
};

// Editorial concept examples, not connected customer implementations.
export const solutionExamples: Record<string, SolutionExample> = {
  insurance: {
    title: 'A home insurance conversation, before the quote.',
    brief:
      'An insurer can place this finder on a home insurance page. The visitor chooses what they want to protect, sees a relevant follow-up and leaves with a preparation checklist for the quote team.',
    question: 'What would you like to protect?',
    branches: [
      {
        label: 'A home I own',
        question: 'Will you live in the property?',
        answers: ['Yes, it is my home', 'No, I rent it out'],
        results: [
          'Prepare the property details and a list of belongings for a buildings and contents conversation.',
          'Prepare the tenancy and property details for a landlord insurance conversation.',
        ],
        next: 'Continue to the insurer’s home quote service',
      },
      {
        label: 'A home I rent',
        question: 'What would you like to discuss?',
        answers: ['My belongings', 'Belongings and liability'],
        results: [
          'Make an inventory of your belongings before discussing contents cover.',
          'Bring your belongings inventory and tenancy questions to an advisor.',
        ],
        next: 'Continue to a contents insurance advisor',
      },
      {
        label: 'An existing policy',
        question: 'What has changed?',
        answers: ['I am moving home', 'I bought new belongings'],
        results: [
          'Have the new address and moving date ready for a policy review.',
          'Prepare details of new items and ask the insurer about existing limits.',
        ],
        next: 'Continue to the insurer’s policy review service',
      },
    ],
    fields: ['Occupancy', 'Protection need', 'Selected next step'],
    logic:
      'Owners answer an occupancy question. Renters choose the topics they want to discuss. Existing policyholders describe what changed. Each path ends with its own preparation checklist.',
    destination:
      'With the visitor’s permission, pass these responses to the configured quote service or CRM. Policy terms and underwriting stay with the insurer.',
    measure:
      'Compare finder starts, completed checklists and clicks to the quote service. Use quote-system reporting to measure completed quotes.',
    boundary: 'Illustrative preparation only. This example does not recommend cover or confirm eligibility.',
  },
  banking: {
    title: 'Prepare a mortgage conversation around the buyer’s next move.',
    brief:
      'A bank can turn a broad mortgage inquiry into a useful advisor brief. Ask about the buying stage, adapt the preparation list and route the visitor to the bank’s appointment or application system.',
    question: 'Where are you in your home buying journey?',
    branches: [
      {
        label: 'Exploring my first home',
        question: 'What would help you prepare?',
        answers: ['Understand the process', 'Prepare for an advisor'],
        results: [
          'Explore the bank’s guide to deposits, fees and the application stages.',
          'Prepare income, regular outgoings and deposit information for an advisor.',
        ],
        next: 'Open the bank’s first home resources',
      },
      {
        label: 'Planning a move',
        question: 'Do you have a property in mind?',
        answers: ['Still looking', 'Yes, I found one'],
        results: [
          'Prepare your current mortgage details and questions about moving.',
          'Bring the property details and your existing mortgage information to an advisor.',
        ],
        next: 'Continue to the bank’s mortgage appointment flow',
      },
      {
        label: 'Reviewing my mortgage',
        question: 'What prompted the review?',
        answers: ['My current term is ending', 'My circumstances changed'],
        results: [
          'Have your current rate, end date and balance ready for a review.',
          'Prepare a summary of what changed and the questions you want to discuss.',
        ],
        next: 'Continue to the bank’s existing customer service',
      },
    ],
    fields: ['Buying stage', 'Preparation need', 'Chosen destination'],
    logic:
      'First-time buyers see preparation resources. Home movers answer whether they have found a property. Existing borrowers choose the reason for a review, which changes the advisor checklist.',
    destination:
      'Send the agreed response fields to an advisor booking or CRM workflow. Applications and affordability checks remain in the bank’s systems.',
    measure:
      'Track completion and appointment clicks. Connect booking outcomes from the bank to assess which journeys lead to attended conversations.',
    boundary: 'Educational example only. No borrowing estimate, credit decision or financial advice is provided.',
  },
  payments: {
    title: 'Let cardholders discover a benefit they can actually use.',
    brief:
      'An issuer can build a benefit explorer for existing cardholders, separate from acquisition and activation. Ask about the next occasion, explain the relevant benefit category and link to current issuer terms.',
    question: 'What are you planning next?',
    branches: [
      {
        label: 'A trip abroad',
        question: 'What would you like to explore?',
        answers: ['Travel preparation', 'Using my card abroad'],
        results: [
          'Review the issuer’s travel benefit information and any activation requirements.',
          'Review foreign currency fees, card controls and the issuer’s overseas support details.',
        ],
        next: 'Open current travel information from the issuer',
      },
      {
        label: 'Everyday spending',
        question: 'Which information matters to you?',
        answers: ['Earning rewards', 'Using existing rewards'],
        results: [
          'Explore eligible spending categories, exclusions and current earning terms.',
          'Explore redemption options and the steps in the issuer’s rewards service.',
        ],
        next: 'Continue to the issuer’s rewards service',
      },
      {
        label: 'Business purchases',
        question: 'What are you organizing?',
        answers: ['Team spending', 'Expense records'],
        results: [
          'Explore the issuer’s employee card and spending control information.',
          'Find the issuer’s guidance on statements, exports and expense tools.',
        ],
        next: 'Open business card support',
      },
    ],
    fields: ['Occasion', 'Benefit interest', 'Terms or service link selected'],
    logic:
      'Travel opens preparation or overseas-use guidance. Everyday spending separates earning from redemption. Business use leads to team-spending or expense-record information.',
    destination:
      'Link to the issuer’s current benefit, wallet or service destination. Do not collect card numbers or credentials in the engagement journey.',
    measure:
      'Measure benefit discovery completion and clicks to terms or activation. Confirm actual activation through issuer reporting, not from a link click.',
    boundary: 'Benefits are illustrative categories, not offers. Availability and terms depend on the issuer and card.',
  },
  healthcare: {
    title: 'Help a health plan member find the right service channel.',
    brief:
      'A health fund or care provider can make service navigation clearer without asking for symptoms. Start with the task, show relevant preparation information and hand over to the organization’s service.',
    question: 'What would you like help with?',
    branches: [
      {
        label: 'Finding a service',
        question: 'How would you prefer to look?',
        answers: ['By location', 'By service name'],
        results: [
          'Continue to the organization’s directory to search locations and opening hours.',
          'Continue to the organization’s directory to find the named service.',
        ],
        next: 'Open the organization’s service directory',
      },
      {
        label: 'Preparing for a visit',
        question: 'What do you need?',
        answers: ['What to bring', 'Where to go'],
        results: [
          'Review the organization’s visit checklist and any instructions in your appointment notice.',
          'Use the appointment notice and location directory to confirm where to go.',
        ],
        next: 'Open appointment preparation information',
      },
      {
        label: 'Understanding my plan',
        question: 'Which resource would help?',
        answers: ['Benefit information', 'Member support'],
        results: [
          'Read the plan’s current benefit information and how to request clarification.',
          'Find the plan’s member service channels and contact hours.',
        ],
        next: 'Continue to member services',
      },
    ],
    fields: ['Service task', 'Preferred resource', 'Destination selected'],
    logic:
      'Directory searches separate location from service name. Visit preparation separates documents from directions. Plan questions lead to benefit information or member support.',
    destination:
      'Hand over to a directory, booking portal or member support service. Define allowed fields with privacy owners before connecting any reporting.',
    measure:
      'Track resource discovery and destination clicks without recording clinical details. Review unresolved service tasks with the communications team.',
    boundary: 'Service navigation only, not medical advice or urgent care. This demo asks for no health information.',
  },
  'real-estate': {
    title: 'Give a project sales team more than a name and phone number.',
    brief:
      'A residential developer can guide a prospect through their preferred home and moving timeline. A brokerage can adapt the same pattern around a listing portfolio, while rental teams use availability and viewing needs.',
    question: 'What are you looking for?',
    branches: [
      {
        label: 'A new family home',
        question: 'Which space matters most?',
        answers: ['Another bedroom', 'Space to work from home'],
        results: [
          'Explore example floor plans with an additional bedroom and compare room dimensions.',
          'Explore example floor plans with a separate room that could serve as a workspace.',
        ],
        next: 'Continue to project floor plans and a viewing request',
      },
      {
        label: 'A rental home',
        question: 'When do you want to move?',
        answers: ['Soon', 'I am planning ahead'],
        results: [
          'Continue to current rental availability and ask the team about viewing dates.',
          'Explore locations and register a preferred moving period with the rental team.',
        ],
        next: 'Continue to the rental team’s availability service',
      },
      {
        label: 'A property investment',
        question: 'What would you like to understand?',
        answers: ['The project', 'The local area'],
        results: [
          'Review project information, specifications and disclosures with the sales team.',
          'Explore transport, amenities and location information before speaking with the team.',
        ],
        next: 'Open the project information pack',
      },
    ],
    fields: ['Buyer or renter intent', 'Space or timing preference', 'Project interest'],
    logic:
      'Buyers compare space priorities, renters choose a moving timeline and investors select project or neighborhood information. These answers determine the content and responsible sales team.',
    destination:
      'Route permissioned inquiry details to the CRM owner for that project or location. Current availability and prices come from the company’s approved source.',
    measure:
      'Measure completed preference journeys and viewing request clicks. Use CRM outcomes to compare inquiries, booked viewings and attended visits.',
    boundary: 'Concept journey only. No live property inventory, availability, price or investment return is shown.',
  },
  retail: {
    title: 'Show how a coffee finder becomes a useful product shortlist.',
    brief:
      'A retailer can replace a generic product quiz with a focused buying conversation. In this example, the shopper’s routine determines the follow-up and the type of coffee equipment to explore.',
    question: 'How do you like to make coffee?',
    branches: [
      {
        label: 'A quick morning cup',
        question: 'What matters more?',
        answers: ['Simple preparation', 'Making several cups'],
        results: [
          'Explore compact brewers with simple setup and clear cleaning instructions.',
          'Explore batch brewers and compare capacity, counter space and cleaning.',
        ],
        next: 'Continue to the retailer’s filtered brewer collection',
      },
      {
        label: 'An espresso ritual',
        question: 'How involved do you want to be?',
        answers: ['I want hands-on control', 'I prefer fewer steps'],
        results: [
          'Explore manual espresso equipment and the grinder and accessories it needs.',
          'Explore automatic espresso machines and compare the daily maintenance steps.',
        ],
        next: 'Continue to the retailer’s espresso collection',
      },
      {
        label: 'A gift for someone',
        question: 'Do they already have a brewer?',
        answers: ['Yes', 'I am not sure'],
        results: [
          'Explore compatible accessories and check the recipient’s brewer model before buying.',
          'Explore starter sets that do not depend on an existing machine.',
        ],
        next: 'Continue to the retailer’s gift collection',
      },
    ],
    fields: ['Coffee routine', 'Preparation preference', 'Collection selected'],
    logic:
      'Morning coffee separates simple preparation from batch brewing. Espresso separates manual control from fewer steps. Gift suggestions depend on whether the recipient already owns a brewer.',
    destination:
      'Map the result to current catalog filters or product pages. Stock, pricing and checkout remain with the ecommerce system.',
    measure:
      'Track finder completion and product clicks. With a configured commerce integration, compare subsequent add-to-cart and purchase events.',
    boundary: 'Illustrative categories only. No live products, stock, prices or customer profiles are used.',
  },
  telecommunications: {
    title: 'Help customers prepare for a plan conversation.',
    brief:
      'A mobile provider can ask about usage before showing a long plan table. Separate individual, family and business needs, then link to current products and the provider’s coverage checks.',
    question: 'Who is the plan for?',
    branches: [
      {
        label: 'Just me',
        question: 'What do you use most?',
        answers: ['Everyday calls and browsing', 'Streaming on the go'],
        results: [
          'Explore individual plans and compare allowances with your usual usage.',
          'Explore data options and read the provider’s usage policies before choosing.',
        ],
        next: 'Continue to current individual plans',
      },
      {
        label: 'My household',
        question: 'What would you like to compare?',
        answers: ['Several mobile lines', 'Mobile and home internet'],
        results: [
          'Compare multi-line options, account management and the terms for each line.',
          'Explore bundled services, then check home internet availability with the provider.',
        ],
        next: 'Continue to household plans and availability checks',
      },
      {
        label: 'My business',
        question: 'Where does the team work?',
        answers: ['Mostly locally', 'Across countries'],
        results: [
          'Prepare line counts and support requirements for the business team.',
          'Prepare destinations and roaming requirements for a business plan discussion.',
        ],
        next: 'Continue to a business sales conversation',
      },
    ],
    fields: ['Customer type', 'Usage priority', 'Plan category selected'],
    logic:
      'Individual users choose a usage priority. Households compare multiple lines or bundled services. Businesses identify local or international needs before speaking with sales.',
    destination:
      'Continue into the provider’s current plan catalog, availability checker or sales workflow. Coverage and eligibility are determined there.',
    measure:
      'Measure completed finders and plan clicks. Use provider reporting to follow orders or sales inquiries beyond the handoff.',
    boundary: 'Illustrative plan categories only. This example does not check coverage, price or eligibility.',
  },
};
